import { useCallback, useMemo } from "react"

import { useStorage } from "src/data/storage/useStorage"
import { localStorageFactory } from "src/utils/storageUtil"

export type ParadiseEntityType = "user" | "home" | "organization" | "device"

interface ParadiseFavoritesState {
  [key: string]: string[] // entityType -> array of entity IDs
}

const paradiseFavoritesStorage = localStorageFactory<ParadiseFavoritesState>({
  key: "minut.paradise.favorites",
})

export function useParadiseFavorites(entityType: ParadiseEntityType) {
  const { data: favorites = {}, set: setFavorites } = useStorage({
    storage: paradiseFavoritesStorage,
  })

  const entityFavorites = useMemo(
    () => (favorites || {})[entityType] || [],
    [favorites, entityType]
  )

  const addFavorite = useCallback(
    (entityId: string) => {
      const currentFavorites = favorites || {}
      const newFavorites = {
        ...currentFavorites,
        [entityType]: [...(currentFavorites[entityType] || []), entityId],
      }
      setFavorites(newFavorites)
    },
    [entityType, favorites, setFavorites]
  )

  const removeFavorite = useCallback(
    (entityId: string) => {
      const currentFavorites = favorites || {}
      const newFavorites = {
        ...currentFavorites,
        [entityType]: (currentFavorites[entityType] || []).filter(
          (id) => id !== entityId
        ),
      }
      setFavorites(newFavorites)
    },
    [entityType, favorites, setFavorites]
  )

  const isFavorite = useCallback(
    (entityId: string) => {
      return entityFavorites.includes(entityId)
    },
    [entityFavorites]
  )

  const toggleFavorite = useCallback(
    (entityId: string) => {
      if (isFavorite(entityId)) {
        removeFavorite(entityId)
      } else {
        addFavorite(entityId)
      }
    },
    [isFavorite, removeFavorite, addFavorite]
  )

  return {
    favorites: entityFavorites,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
  }
}
